import dayjs, { Dayjs } from 'dayjs';

type ContractPeriod = {
  validFrom: string | Date;
  validUntil?: string | Date | null;
};

type SchoolYearPeriod = {
  start: string | Date;
  end: string | Date;
};

const startsOnOrBefore = (contract: ContractPeriod, day: Dayjs) => dayjs(contract.validFrom).isSameOrBefore(day, 'day');
const endsOnOrAfter = (contract: ContractPeriod, day: Dayjs) =>
  !contract.validUntil || dayjs(contract.validUntil).isSameOrAfter(day, 'day');

const byValidFrom = (a: ContractPeriod, b: ContractPeriod) =>
  dayjs(a.validFrom).valueOf() - dayjs(b.validFrom).valueOf();

/** Alle Verträge, die mindestens einen Tag im Schuljahr gültig sind – nach Beginn sortiert. */
export function getContractsInSchoolYear<T extends ContractPeriod>(contracts: T[], schoolYear: SchoolYearPeriod): T[] {
  const start = dayjs(schoolYear.start);
  const end = dayjs(schoolYear.end);
  return contracts.filter((c) => startsOnOrBefore(c, end) && endsOnOrAfter(c, start)).sort(byValidFrom);
}

/**
 * Der Vertrag, mit dem die Deputate eines Schuljahres abgerechnet werden: unter den Verträgen, die mindestens
 * einen Tag im Schuljahr gültig sind, der mit dem spätesten Beginn – dieselbe Regel wie in den Deputats-Berichten
 * (`Person.currentContract` im Backend). Ohne Schuljahr gibt es keinen.
 */
export function selectSchoolYearContract<T extends ContractPeriod>(
  contracts: T[],
  schoolYear: SchoolYearPeriod | null | undefined,
): T | undefined {
  if (!schoolYear) return undefined;
  return getContractsInSchoolYear(contracts, schoolYear).at(-1);
}

/**
 * Der Vertrag, dessen Werte in den Personenlisten angezeigt werden:
 * - Stichtag ist heute, im gewählten Schuljahr geklemmt – für vergangene Schuljahre also
 *   dessen Ende, für zukünftige dessen Beginn.
 * - Gilt am Stichtag ein Vertrag, wird dieser genommen.
 * - Sonst der nächste, der im Schuljahr noch beginnt (zukünftiges Schuljahr: der erstgültige),
 * - sonst der letzte, der im Schuljahr schon geendet hat (vergangenes Schuljahr: der letztgültige).
 *
 * Ohne Schuljahr zählt nur der heute gültige Vertrag.
 */
export function selectDisplayedContract<T extends ContractPeriod>(
  contracts: T[],
  schoolYear: SchoolYearPeriod | null | undefined,
  today: Dayjs = dayjs(),
): T | undefined {
  if (!schoolYear) {
    return contracts
      .filter((c) => startsOnOrBefore(c, today) && endsOnOrAfter(c, today))
      .sort(byValidFrom)
      .at(-1);
  }

  const start = dayjs(schoolYear.start);
  const end = dayjs(schoolYear.end);
  const referenceDay = today.isBefore(start, 'day') ? start : today.isAfter(end, 'day') ? end : today;

  const inSchoolYear = getContractsInSchoolYear(contracts, schoolYear);

  const validOnReferenceDay = inSchoolYear
    .filter((c) => startsOnOrBefore(c, referenceDay) && endsOnOrAfter(c, referenceDay))
    .at(-1);
  if (validOnReferenceDay) return validOnReferenceDay;

  const upcoming = inSchoolYear.find((c) => dayjs(c.validFrom).isAfter(referenceDay, 'day'));
  if (upcoming) return upcoming;

  return inSchoolYear.filter((c) => !endsOnOrAfter(c, referenceDay)).at(-1);
}
